import type { AxiosRequestConfig } from "axios";

import apiClient from "./client";

interface EtagEntry<T> {
  etag: string;
  data: T;
}

const entries = new Map<string, EtagEntry<unknown>>();

function getHeaderValue(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export async function getWithEtag<T>(
  url: string,
  cacheKey: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const cached = entries.get(cacheKey) as EtagEntry<T> | undefined;
  const headers = {
    ...(config?.headers ?? {}),
    ...(cached ? { "If-None-Match": cached.etag } : {}),
  };

  const response = await apiClient.get<T>(url, {
    ...config,
    headers,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  });

  if (response.status === 304) {
    if (!cached) {
      throw new Error(`Received 304 for uncached reference data: ${url}`);
    }
    return cached.data;
  }

  const etag = getHeaderValue(response.headers.etag);
  if (etag) {
    entries.set(cacheKey, { etag, data: response.data });
  }

  return response.data;
}